import { type ReactNode, useState } from 'react';
import cn from 'clsx';
import { innerText } from '@pkgs/components/utils/innerText';
import { type InputTextSizesProps } from '@ev-tech/ui-core-components/InputText';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Select as BaseSelect } from '@base-ui/react/select';
import { type Align, type Side } from '@base-ui/react/internals/useAnchorPositioning';

import { SelectOptions } from './SelectOptions';
import { SelectButton } from './SelectButton';
import { type ArrowVariant, type SelectButtonVariants, type SelectItem } from './Select.types';

import styles from './Select.module.css';

export type { SelectItem } from './Select.types';

type SelectComponentProps = {
  buttonIcon?: ReactNode;
  arrowVariant?: ArrowVariant;
  placeholder?: string;
  buttonLabelOverride?: string;
  label: string;
  className?: string;
  items: SelectItem[];
  accessibilityLabel: string;
  hasLabelHidden?: boolean;
  size?: InputTextSizesProps;
  isInModal?: boolean;
  side?: Side;
  align?: Align;
  offset?: number;
  variant?: SelectButtonVariants;
  usesButtonWidth?: boolean;
  shouldHideArrowIcon?: boolean;
  'data-testid'?: string;
  'data-hidden'?: boolean;
  value?: string | null;
  defaultValue?: string | null;
  onChange?: (value: string | null) => void;
  name?: string;
  disabled?: boolean;
  hasError?: boolean;
};

const getHiddenAriaLabel = ({
  items,
  selectedValue,
  placeholder,
  accessibilityLabel,
}: {
  items: SelectItem[];
  selectedValue?: string | null;
  placeholder?: string;
  accessibilityLabel: string;
}): string => {
  const selectedItem = items.find((item) => item.id === selectedValue);
  // NS-8119 Hidden a11y label always needs to start with the visible label
  if (!selectedItem) return `${placeholder} - ${accessibilityLabel}`;
  if (selectedItem.selectedLabel) return `${innerText(selectedItem.selectedLabel)} - ${accessibilityLabel}`;
  return `${innerText(selectedItem.label)} - ${accessibilityLabel}`;
};

export const Select = ({
  buttonIcon,
  arrowVariant = 'medium',
  placeholder,
  buttonLabelOverride,
  className,
  label,
  items,
  hasLabelHidden,
  size,
  side = 'bottom',
  align = 'start',
  offset = 4,
  variant,
  usesButtonWidth = false,
  shouldHideArrowIcon = false,
  accessibilityLabel,
  'data-testid': dataTestId,
  'data-hidden': dataHidden,
  value,
  defaultValue,
  onChange,
  name,
  disabled,
  hasError,
}: SelectComponentProps) => {
  const [open, setOpen] = useState(false);

  const hiddenAriaLabel = hasLabelHidden
    ? getHiddenAriaLabel({
        items,
        selectedValue: value !== undefined ? value : defaultValue,
        placeholder,
        accessibilityLabel,
      })
    : undefined;

  return (
    <div className={cn(styles.select, className)} data-hidden={dataHidden} data-testid={dataTestId}>
      <BaseSelect.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={(nextValue) => onChange?.(nextValue)}
        open={open}
        onOpenChange={setOpen}
        modal
        disabled={disabled}
        name={name}
      >
        {hasLabelHidden ? null : (
          <BaseSelect.Label>
            <Copy className={styles.copy} size="m" text={label} />
          </BaseSelect.Label>
        )}
        <SelectButton
          buttonIcon={buttonIcon}
          buttonLabelOverride={buttonLabelOverride}
          placeholder={placeholder}
          items={items}
          ariaLabel={hiddenAriaLabel}
          size={size}
          arrowVariant={arrowVariant}
          variant={variant}
          shouldHideArrowIcon={shouldHideArrowIcon}
          hasError={hasError}
          data-testid={dataTestId ? `${dataTestId}_button` : 'search-components_select_button'}
        />
        <SelectOptions
          items={items}
          usesButtonWidth={usesButtonWidth}
          dataTestId={dataTestId}
          side={side}
          align={align}
          sideOffset={offset}
        />
      </BaseSelect.Root>
    </div>
  );
};
